import { createContext, useContext } from "react";
import { Node, mergeAttributes, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { useParams, useHistory } from "react-router-dom";
import { FileIcon } from "../FileIcon";
import {
  FileType,
  PermissionsResourceType,
  useDataRoomFileQuery,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { links } from "@/src/utils/links";
import { PermissionCheck } from "../permissions/PermissionCheck";
import {
  rectToExcelRange,
  rectToExcelCell,
} from "@/src/utils/rectToExcelRange";

export interface FileRefClickData {
  fileId: string;
  fileName: string;
  fileType: FileType;
  folderId: string;
  pageIndex?: number;
  rectsOnPage?: number[][];
  sheetIndex?: number;
  cell?: string;
}

const FileRefClickContext = createContext<((data: FileRefClickData) => void) | null>(null);
export const FileRefClickProvider = FileRefClickContext.Provider;

function FileRefView({ node, selected, editor }: { node: any; selected: boolean; editor: any }) {
  const editable = editor?.isEditable ?? true;
  const {
    fileId,
    pageIndex: rawPage,
    rectsOnPage: rawRects,
    sheetIndex: rawSheet,
    row0: rawRow0,
    col0: rawCol0,
    row1: rawRow1,
    col1: rawCol1,
  } = node.attrs;

  const client = useGqlClient();
  const { dealId } = useParams<{ dealId: string }>();
  const history = useHistory();
  const onFileClick = useContext(FileRefClickContext);
  const fileQuery = useDataRoomFileQuery(
    client,
    { id: fileId },
    {
      enabled: fileId !== "",
      queryKey: ["InlineFilePill", { id: fileId }],
    },
  );

  const name = fileQuery.data?.dataRoomFile.name ?? fileId;
  const fileType = fileQuery.data?.dataRoomFile.fileType ?? FileType.Other;
  const folderId = fileQuery.data?.dataRoomFile.folderId ?? "";

  const pageIndex = rawPage !== "" ? parseInt(rawPage, 10) : undefined;
  const sheetIndex = rawSheet !== "" ? parseInt(rawSheet, 10) : undefined;

  let rectsOnPage: number[][] | undefined;
  if (rawRects) {
    try {
      const parsed = JSON.parse(rawRects);
      if (Array.isArray(parsed)) rectsOnPage = parsed;
    } catch { /* ignore malformed */ }
  }

  let cell: string | undefined;
  if (rawRow0 !== "" && rawCol0 !== "") {
    const row0 = parseInt(rawRow0, 10);
    const col0 = parseInt(rawCol0, 10);
    if (rawRow1 !== "" && rawCol1 !== "") {
      cell = rectToExcelRange({
        row0,
        col0,
        row1: parseInt(rawRow1, 10),
        col1: parseInt(rawCol1, 10),
      });
    } else {
      cell = rectToExcelCell({ row0, col0 });
    }
  }

  let locationLabel: string | null = null;
  if (pageIndex !== undefined) {
    locationLabel = `p.${pageIndex + 1}`;
  } else if (cell) {
    locationLabel = cell;
  }

  const wrapperClasses = `inline-flex items-center align-middle gap-x-0.5 mx-0.5 -my-0.5 max-w-[14rem] rounded px-1 py-0.5 text-xs select-none transition-colors duration-75 ${selected
      ? "bg-persian-200 text-persian-900 ring-2 ring-persian-300/60"
      : "bg-persian-50 text-persian-700 hover:bg-persian-100 hover:text-persian-900"
    }`;

  const isInteractive = !editable && (!!onFileClick || (!!dealId && !!folderId));

  const handleClick = () => {
    if (!isInteractive) return;

    if (onFileClick) {
      onFileClick({
        fileId,
        fileName: name,
        fileType,
        folderId,
        pageIndex,
        rectsOnPage,
        sheetIndex,
        cell,
      });
    } else {
      history.push(
        links.fileLink(fileId, folderId, dealId!, {
          pageIndex,
          rectsOnPage,
          sheetIndex,
          cell,
        }),
      );
    }
  };

  const pill = (
    <span
      role={isInteractive ? "button" : undefined}
      className={`${wrapperClasses} ${isInteractive ? "cursor-pointer" : ""}`}
      contentEditable={false}
      onClick={handleClick}
    >
      <FileIcon fileType={fileType} size="s" />
      <span className="font-medium truncate" title={name}>
        {name}
      </span>
      {locationLabel && (
        <span className="text-gray-400 flex-shrink-0">{locationLabel}</span>
      )}
    </span>
  );

  if (!onFileClick && !!dealId && !!folderId) {
    return (
      <NodeViewWrapper as="span" className="inline">
        <PermissionCheck
          input={{
            permissionKey: "documents:view",
            resource: {
              resourceID: fileId,
              resourceType: PermissionsResourceType.Document,
            },
          }}
        >
          {pill}
        </PermissionCheck>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper as="span" className="inline">
      {pill}
    </NodeViewWrapper>
  );
}

const FILE_REF_RE =
  /^\{\{dtrmfile:(dtrmfile_[A-Za-z0-9]+)([^}]*)\}\}/;

function parseRefParams(raw: string): Record<string, string> {
  const params: Record<string, string> = {};
  if (!raw) return params;
  const segments = raw.split("|").filter(Boolean);
  for (const seg of segments) {
    const colonIdx = seg.indexOf(":");
    if (colonIdx > 0) {
      params[seg.slice(0, colonIdx)] = seg.slice(colonIdx + 1);
    }
  }
  return params;
}

function buildRefString(attrs: Record<string, any>): string {
  const fileId = attrs.fileId || "";
  const paramParts: string[] = [];
  for (const key of [
    "pageIndex",
    "rectsOnPage",
    "sheetIndex",
    "row0",
    "col0",
    "row1",
    "col1",
  ]) {
    const val = attrs[key];
    if (val !== undefined && val !== "") {
      paramParts.push(`${key}:${val}`);
    }
  }
  return `{{dtrmfile:${fileId}${paramParts.length ? "|" + paramParts.join("|") : ""}}}`;
}

const ATTR_KEYS = [
  "pageIndex",
  "rectsOnPage",
  "sheetIndex",
  "row0",
  "col0",
  "row1",
  "col1",
] as const;

export const FileRefExtension = Node.create({
  name: "fileRef",
  group: "inline",
  inline: true,
  atom: true,

  addAttributes() {
    return {
      fileId: { default: "" },
      pageIndex: { default: "" },
      rectsOnPage: { default: "" },
      sheetIndex: { default: "" },
      row0: { default: "" },
      col0: { default: "" },
      row1: { default: "" },
      col1: { default: "" },
    };
  },

  parseHTML() {
    return [{ tag: "file-ref" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["file-ref", mergeAttributes(HTMLAttributes)];
  },

  renderText({ node }) {
    return buildRefString(node.attrs);
  },

  markdownTokenizer: {
    name: "fileRef",
    level: "inline",
    start: "{{",
    // @ts-expect-error -- custom field consumed by @tiptap/markdown via getExtensionField
    tokenize(src: string) {
      const match = FILE_REF_RE.exec(src);
      if (!match) return null;
      const params = parseRefParams(match[2]);
      return {
        type: "fileRef",
        raw: match[0],
        fileId: match[1],
        ...params,
      };
    },
  },

  parseMarkdown(token: any, helpers: any) {
    const attrs: Record<string, string> = { fileId: token.fileId || "" };
    for (const key of ATTR_KEYS) {
      attrs[key] = token[key] || "";
    }
    return helpers.createNode("fileRef", attrs);
  },

  renderMarkdown(node: any) {
    return buildRefString(node.attrs || {});
  },

  addNodeView() {
    return ReactNodeViewRenderer(FileRefView);
  },
});
